// components/ManualAdjustments.js - NEW COMPONENT
import React, { useState, useEffect } from 'react';
import { Calculator, Plus, Minus, RotateCcw, TrendingUp, TrendingDown } from 'lucide-react';

const ManualAdjustments = ({ comparableData, onAdjustmentsChange }) => {
  const [adjustments, setAdjustments] = useState({
    pool: { enabled: false, value: 5000, description: 'Swimming Pool' },
    garage: { enabled: false, value: 3000, description: 'Garage Space (per car)' },
    lotSize: { enabled: false, value: 0, description: 'Lot Size Difference' },
    condition: { enabled: false, value: 0, description: 'Property Condition' },
    age: { enabled: false, value: 0, description: 'Age/Year Built' },
    bedrooms: { enabled: false, value: 2500, description: 'Bedroom Count Difference' },
    bathrooms: { enabled: false, value: 1500, description: 'Bathroom Count Difference' },
    sqft: { enabled: false, value: 50, description: 'Square Footage (per sqft)' },
    view: { enabled: false, value: 7500, description: 'Premium View' },
    updates: { enabled: false, value: 10000, description: 'Recent Updates/Renovations' }
  });

  const [adjustedComparables, setAdjustedComparables] = useState([]);

  // Preset adjustment values based on market standards
  const presetAdjustments = {
    conservative: {
      pool: 3000,
      garage: 2000,
      bedrooms: 2000,
      bathrooms: 1000,
      sqft: 30,
      view: 5000,
      updates: 7500
    },
    moderate: {
      pool: 5000,
      garage: 3000,
      bedrooms: 2500,
      bathrooms: 1500,
      sqft: 50,
      view: 7500,
      updates: 10000
    },
    aggressive: {
      pool: 8000,
      garage: 4000,
      bedrooms: 3000,
      bathrooms: 2000,
      sqft: 75,
      view: 10000,
      updates: 15000
    }
  };

  // Calculate adjusted prices when adjustments change
  useEffect(() => {
    if (!comparableData || comparableData.length === 0) return;

    const adjusted = comparableData.map(comp => {
      let adjustedPrice = comp.price;
      let totalAdjustment = 0;
      const appliedAdjustments = [];

      // Apply each enabled adjustment
      Object.entries(adjustments).forEach(([key, adjustment]) => {
        if (adjustment.enabled) {
          let adjustmentValue = 0;

          switch (key) {
            case 'pool':
              // Assuming subject has pool if adjustment is enabled
              adjustmentValue = adjustment.value;
              break;
            case 'garage':
              // Adjust based on garage difference
              adjustmentValue = adjustment.value;
              break;
            case 'sqft':
              // Calculate per square foot difference
              const sqftDiff = (comp.livingArea || 0) - 2000; // Assume 2000 sqft subject
              adjustmentValue = sqftDiff * adjustment.value;
              break;
            case 'bedrooms':
              // Adjust for bedroom difference
              const bedroomDiff = (comp.beds || 0) - 3; // Assume 3 bedroom subject
              adjustmentValue = bedroomDiff * adjustment.value;
              break;
            case 'bathrooms':
              // Adjust for bathroom difference
              const bathroomDiff = (comp.baths || 0) - 2; // Assume 2 bathroom subject
              adjustmentValue = bathroomDiff * adjustment.value;
              break;
            default:
              adjustmentValue = adjustment.value;
          }

          totalAdjustment += adjustmentValue;
          appliedAdjustments.push({
            type: key,
            description: adjustment.description,
            value: adjustmentValue
          });
        }
      });

      adjustedPrice += totalAdjustment;

      return {
        ...comp,
        originalPrice: comp.price,
        adjustedPrice: adjustedPrice,
        totalAdjustment: totalAdjustment,
        appliedAdjustments: appliedAdjustments
      };
    });

    setAdjustedComparables(adjusted);
    
    // Notify parent component
    if (onAdjustmentsChange) {
      onAdjustmentsChange(adjusted);
    }
  }, [adjustments, comparableData]);

  const toggleAdjustment = (key) => {
    setAdjustments(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        enabled: !prev[key].enabled
      }
    }));
  };

  const updateAdjustmentValue = (key, value) => {
    setAdjustments(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        value: parseFloat(value) || 0
      }
    }));
  };

  const applyPreset = (presetType) => {
    const preset = presetAdjustments[presetType];
    setAdjustments(prev => {
      const updated = { ...prev };
      Object.entries(preset).forEach(([key, value]) => {
        if (updated[key]) {
          updated[key].value = value;
        }
      });
      return updated;
    });
  };

  const resetAdjustments = () => {
    setAdjustments(prev => {
      const reset = { ...prev };
      Object.keys(reset).forEach(key => {
        reset[key].enabled = false;
      });
      return reset;
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  return (
    <div className="space-y-6">
      {/* Header with Presets */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h3 className="text-xl font-semibold text-white mb-2">Manual Adjustments</h3>
          <p className="text-gray-400 text-sm">
            Apply adjustments to comparable properties based on feature differences
          </p>
        </div>
        
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => applyPreset('conservative')}
            className="px-3 py-1 bg-blue-900 text-blue-200 rounded text-sm hover:bg-blue-800"
          >
            Conservative
          </button>
          <button
            onClick={() => applyPreset('moderate')}
            className="px-3 py-1 bg-green-900 text-green-200 rounded text-sm hover:bg-green-800"
          >
            Moderate
          </button>
          <button
            onClick={() => applyPreset('aggressive')}
            className="px-3 py-1 bg-orange-900 text-orange-200 rounded text-sm hover:bg-orange-800"
          >
            Aggressive
          </button>
          <button
            onClick={resetAdjustments}
            className="px-3 py-1 bg-gray-700 text-gray-300 rounded text-sm hover:bg-gray-600 flex items-center gap-1"
          >
            <RotateCcw className="w-3 h-3" />
            Reset
          </button>
        </div>
      </div>

      {/* Adjustment Controls */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {Object.entries(adjustments).map(([key, adjustment]) => (
          <div
            key={key}
            className={`p-4 rounded-lg border transition-colors ${
              adjustment.enabled
                ? 'bg-blue-900 border-blue-600'
                : 'bg-gray-800 border-gray-700'
            }`}
          >
            <div className="flex items-center justify-between mb-3">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={adjustment.enabled}
                  onChange={() => toggleAdjustment(key)}
                  className="mr-2"
                />
                <span className="font-medium text-gray-200">
                  {adjustment.description}
                </span>
              </label>
            </div>

            {adjustment.enabled && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-400">$</span>
                  <input
                    type="number"
                    value={adjustment.value}
                    onChange={(e) => updateAdjustmentValue(key, e.target.value)}
                    className="flex-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm"
                    placeholder="0"
                  />
                  {key === 'sqft' && (
                    <span className="text-sm text-gray-400">per sqft</span>
                  )}
                </div>
                <div className="text-xs text-gray-500">
                  {adjustment.value > 0 ? (
                    <span className="text-green-400">+{formatCurrency(adjustment.value)}</span>
                  ) : adjustment.value < 0 ? (
                    <span className="text-red-400">{formatCurrency(adjustment.value)}</span>
                  ) : (
                    'No adjustment'
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Summary of Active Adjustments */}
      {Object.values(adjustments).some(adj => adj.enabled) && (
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
          <h4 className="font-semibold text-gray-200 mb-3 flex items-center gap-2">
            <Calculator className="w-4 h-4" />
            Active Adjustments Summary
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {Object.entries(adjustments)
              .filter(([_, adj]) => adj.enabled)
              .map(([key, adjustment]) => (
                <div key={key} className="flex justify-between items-center">
                  <span className="text-sm text-gray-400">{adjustment.description}:</span>
                  <span className={`text-sm font-medium ${
                    adjustment.value > 0 ? 'text-green-400' : 
                    adjustment.value < 0 ? 'text-red-400' : 'text-gray-300'
                  }`}>
                    {adjustment.value > 0 ? '+' : ''}{formatCurrency(adjustment.value)}
                  </span>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Adjusted Comparables Preview */}
      {adjustedComparables.length > 0 && (
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
          <h4 className="font-semibold text-gray-200 mb-4">Adjusted Comparable Prices</h4>
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {adjustedComparables.slice(0, 5).map((comp, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-700 rounded">
                <div className="flex-1">
                  <div className="text-sm text-gray-300 truncate">
                    {comp.address}
                  </div>
                  <div className="text-xs text-gray-500">
                    {comp.beds}bd/{comp.baths}ba • {comp.livingArea?.toLocaleString()} sqft
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-medium text-white">
                    {formatCurrency(comp.adjustedPrice)}
                  </div>
                  {comp.totalAdjustment !== 0 && (
                    <div className={`text-xs flex items-center gap-1 ${
                      comp.totalAdjustment > 0 ? 'text-green-400' : 'text-red-400'
                    }`}>
                      {comp.totalAdjustment > 0 ? (
                        <TrendingUp className="w-3 h-3" />
                      ) : (
                        <TrendingDown className="w-3 h-3" />
                      )}
                      {comp.totalAdjustment > 0 ? '+' : ''}{formatCurrency(comp.totalAdjustment)}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ManualAdjustments;